import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface TaskDefinition {
  id: string;
  name: string;
  description: string;
  schedule: string;
  lastRun?: string;
  nextRun?: string;
  status?: 'success' | 'error' | 'running' | 'pending';
  lastResult?: string;
}

interface TaskLog {
  timestamp: string;
  tasks_run: string[];
  errors: string[];
  duration?: number;
}

const ScheduledTasks: React.FC = () => {
  const { member } = useAuth();
  const [tasks, setTasks] = useState<TaskDefinition[]>([]);
  const [taskDefinitions, setTaskDefinitions] = useState<TaskDefinition[]>([]);
  const [taskLogs, setTaskLogs] = useState<TaskLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningTask, setRunningTask] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'info' });
  const [logDialog, setLogDialog] = useState({ open: false, taskId: '', logs: [] as TaskLog[] });
  const [secretDialog, setSecretDialog] = useState({ open: false, secret: '' });
  const userTimezone = member?.timezone || 'America/Toronto';

  useEffect(() => {
    fetchTaskStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchTaskStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTaskStatus = async () => {
    try {
      const response = await api.get('/endpoints/scheduled_tasks_status');
      
      if (response.data.success) {
        // Get task definitions from API
        const definitions = response.data.task_definitions || [];
        setTaskDefinitions(definitions);
        
        // Map task definitions with actual status from the database
        const tasksWithStatus = definitions.map((task: any) => {
          const taskStatus = response.data.task_status?.[task.id] || {};
          const lastRun = taskStatus.last_run;
          const lastSuccess = taskStatus.last_success;
          const lastError = taskStatus.last_error;
          
          let status: 'success' | 'error' | 'pending' = 'pending';
          if (lastRun) {
            status = lastError && (!lastSuccess || new Date(lastError) > new Date(lastSuccess)) ? 'error' : 'success';
          }
          
          return {
            ...task,
            status,
            lastRun: lastRun || undefined,
            nextRun: calculateNextRun(task.schedule),
            lastResult: lastError && (!lastSuccess || new Date(lastError) > new Date(lastSuccess)) 
              ? 'Error on last run' 
              : lastSuccess ? 'Success' : 'Never run',
          };
        });
        
        setTasks(tasksWithStatus);
        
        // Update task logs if available
        if (response.data.logs) {
          setTaskLogs(response.data.logs.map((log: any) => ({
            timestamp: log.executed_at,
            tasks_run: JSON.parse(log.tasks_run || '[]'),
            errors: JSON.parse(log.errors || '[]'),
            duration: log.duration_ms,
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching task status:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateNextRun = (schedule: string): string => {
    const now = new Date();
    
    if (schedule.includes('12:05 AM')) {
      const next = new Date(now);
      next.setHours(0, 5, 0, 0);
      if (next <= now) {
        next.setDate(next.getDate() + 1);
      }
      return next.toISOString();
    }
    
    if (schedule.includes('1st of each month')) {
      const next = new Date(now);
      next.setDate(1);
      next.setHours(0, 10, 0, 0);
      if (next <= now) {
        next.setMonth(next.getMonth() + 1);
      }
      return next.toISOString();
    }
    
    if (schedule.includes('28th of each month')) {
      const next = new Date(now);
      next.setDate(28);
      next.setHours(10, 0, 0, 0);
      if (next <= now) {
        next.setMonth(next.getMonth() + 1);
      }
      return next.toISOString();
    }
    
    if (schedule.includes('3:00 AM')) {
      const next = new Date(now);
      next.setHours(3, 0, 0, 0);
      if (next <= now) {
        next.setDate(next.getDate() + 1);
      }
      return next.toISOString();
    }
    
    if (schedule.includes('Every 15 minutes')) {
      const next = new Date(now);
      const minutes = Math.ceil(next.getMinutes() / 15) * 15;
      next.setMinutes(minutes, 0, 0);
      return next.toISOString();
    }
    
    if (schedule.includes('Every 10 minutes')) {
      const next = new Date(now);
      const minutes = Math.ceil(next.getMinutes() / 10) * 10;
      next.setMinutes(minutes, 0, 0);
      return next.toISOString();
    }
    
    return new Date(now.getTime() + 3600000).toISOString(); // Default: 1 hour from now
  };

  const handleRunTask = async (taskId: string) => {
    setSecretDialog({ open: true, secret: '' });
  };

  const confirmRunTask = async () => {
    if (!secretDialog.secret) {
      setSnackbar({ open: true, message: 'Please enter the secret key', severity: 'error' });
      return;
    }

    setSecretDialog({ open: false, secret: '' });
    setRunningTask('manual');
    
    try {
      const response = await api.post('/endpoints/scheduled_tasks', {
        secret: secretDialog.secret,
        force_run: true,
      });
      
      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `Tasks executed: ${response.data.tasks_run.length} tasks completed`, 
          severity: 'success' 
        });
        
        // Update task logs
        const newLog: TaskLog = {
          timestamp: response.data.timestamp,
          tasks_run: response.data.tasks_run,
          errors: response.data.errors,
        };
        setTaskLogs(prev => [newLog, ...prev.slice(0, 99)]); // Keep last 100 logs
      } else {
        setSnackbar({ 
          open: true, 
          message: 'Task execution failed: ' + (response.data.errors?.join(', ') || 'Unknown error'), 
          severity: 'error' 
        });
      }
      
      fetchTaskStatus();
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to run tasks', 
        severity: 'error' 
      });
    } finally {
      setRunningTask(null);
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'success':
        return <CheckIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'running':
        return <CircularProgress size={16} />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDateTime = (isoString?: string) => {
    if (!isoString) return 'Never';
    
    // MySQL timestamps come without timezone info
    // With SET time_zone = '+00:00', they are now in UTC
    let dateStr = isoString;
    
    // Handle MySQL datetime format (YYYY-MM-DD HH:MM:SS)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      // This is a MySQL datetime in UTC, add Z to indicate UTC
      dateStr = dateStr.replace(' ', 'T') + 'Z';
    } else if (!dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-')) {
      // Other formats without timezone, assume UTC
      dateStr = dateStr + 'Z';
    }
    
    const date = new Date(dateStr);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleString('en-US', {
      timeZone: userTimezone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h2">
              Scheduled Tasks
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchTaskStatus}
            >
              Refresh
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Tasks run automatically based on their schedule. The scheduler checks every minute for tasks to execute.
            </Typography>
          </Alert>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Run</TableCell>
                  <TableCell>Next Run</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{task.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {task.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{task.schedule}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(task.status)}
                        label={task.status || 'Pending'}
                        size="small"
                        color={getStatusColor(task.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(task.lastRun)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(task.nextRun)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Logs">
                        <IconButton
                          size="small"
                          onClick={() => setLogDialog({ open: true, taskId: task.id, logs: taskLogs })}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Recent Task Executions */}
          {taskLogs.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Recent Executions
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                All times displayed in your local timezone ({userTimezone.split('/')[1].replace('_', ' ')})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Tasks Run</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {taskLogs.slice(0, 10).map((log, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                        <TableCell>{log.tasks_run.join(', ') || 'None'}</TableCell>
                        <TableCell>
                          {log.errors.length > 0 ? (
                            <Chip label="Error" color="error" size="small" />
                          ) : (
                            <Chip label="Success" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Task Logs Dialog */}
      <Dialog
        open={logDialog.open}
        onClose={() => setLogDialog({ open: false, taskId: '', logs: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Task Execution Logs</DialogTitle>
        <DialogContent>
          {logDialog.logs.length === 0 ? (
            <Typography color="textSecondary">No logs available</Typography>
          ) : (
            <Box>
              {logDialog.logs.map((log, index) => (
                <Box key={index} mb={2} p={2} bgcolor="grey.100" borderRadius={1}>
                  <Typography variant="body2">
                    <strong>Time:</strong> {formatDateTime(log.timestamp)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tasks:</strong> {log.tasks_run.join(', ') || 'None'}
                  </Typography>
                  {log.errors.length > 0 && (
                    <Typography variant="body2" color="error">
                      <strong>Errors:</strong> {log.errors.join(', ')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialog({ open: false, taskId: '', logs: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret Key Dialog */}
      <Dialog
        open={secretDialog.open}
        onClose={() => setSecretDialog({ open: false, secret: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Secret Key</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Secret Key"
            value={secretDialog.secret}
            onChange={(e) => setSecretDialog({ ...secretDialog, secret: e.target.value })}
            margin="normal"
            helperText="Enter the scheduled tasks secret key to run tasks manually"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecretDialog({ open: false, secret: '' })}>
            Cancel
          </Button>
          <Button onClick={confirmRunTask} variant="contained" color="primary">
            Run Tasks
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduledTasks;